import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import preMarketDiagnosticsService from "@/services/preMarketDiagnosticsService";
import type { AlgoConfigDiagnostic, DiagnosticCheck, PreMarketDiagnosticsResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

/**
 * Administration > Pre-Market Diagnostics -- a one-click version of the
 * manual DB-inspection checklist run by hand during the 2026-09-02
 * 915-model go-live (kill-switch layers, exactly-one-active-config-per-algo
 * with a specific check for the wrong-strategy-template mixup that hit two
 * algo_config rows that day, each algo's customer activation status, Dhan
 * instrument master freshness, and every referenced broker session's
 * health), plus (added 2026-09-07/09-08, from the TEK_915_ITM_BUY
 * zero-trade incident) the Algo Engine live feed's flag+heartbeat, a
 * proactive Breeze session login probe, a narrow-entry-window risk flag,
 * and backend restart proximity, plus (added 2026-09-08, follow-up
 * hardening) a timed database connectivity check and a candle formation
 * health check (staleness/OHLC sanity/volume on the most recently
 * persisted algo_candle row). Entirely read-only in the UI sense --
 * surfaces problems, does not fix them; any fix (reactivating a config
 * version, topping up broker funds, refreshing the Breeze token, etc.)
 * stays a deliberate separate action.
 */

const STATUS_COLOR: Record<string, "success" | "warning" | "error"> = {
  OK: "success",
  WARN: "warning",
  FAIL: "error",
};

function StatusChip({ status }: { status: string }) {
  return <Chip label={status} color={STATUS_COLOR[status] ?? "default"} size="small" />;
}

function CheckCard({ check }: { check: DiagnosticCheck }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {check.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {check.message}
            </Typography>
          </Box>
          <StatusChip status={check.status} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export function PreMarketDiagnosticsPage() {
  const [result, setResult] = useState<PreMarketDiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await preMarketDiagnosticsService.run();
      setResult(data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Pre-Market Diagnostics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only readiness check for the live algo trading system. Run this before market open.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayCircleOutlineIcon />}
          onClick={handleRun}
          disabled={loading}
        >
          {loading ? "Running…" : "Run Diagnostics"}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Stack spacing={2}>
          <Alert severity={STATUS_COLOR[result.overall_status] ?? "info"} sx={{ fontWeight: 600 }}>
            Overall status: {result.overall_status} — generated at {new Date(result.generated_at).toLocaleString()}
          </Alert>

          <CheckCard check={result.backend_health} />
          <CheckCard check={result.database_connectivity} />
          <CheckCard check={result.kill_switch} />
          <CheckCard check={result.instrument_master} />
          <CheckCard check={result.dhan_session} />
          <CheckCard check={result.live_feed} />
          <CheckCard check={result.candle_formation} />
          <CheckCard check={result.breeze_session_validity} />
          <CheckCard check={result.narrow_window_risk} />
          <CheckCard check={result.recent_restart} />

          <Divider />
          <Typography variant="h6" fontWeight={600}>
            Algo Configs
          </Typography>
          <Card variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Algo</TableCell>
                  <TableCell>Strategy Key</TableCell>
                  <TableCell>Active Version</TableCell>
                  <TableCell>Customer Activation</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.algo_configs.map((algo: AlgoConfigDiagnostic) => (
                  <TableRow key={algo.algo_id}>
                    <TableCell>{algo.algo_name}</TableCell>
                    <TableCell>{algo.strategy_key ?? "—"}</TableCell>
                    <TableCell>
                      {algo.active_version ?? "—"}
                      {algo.active_version_count > 1 && (
                        <Typography component="span" color="error" sx={{ ml: 1 }}>
                          ({algo.active_version_count} active!)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{algo.customer_activation_status ?? "none"}</TableCell>
                    <TableCell>
                      <StatusChip status={algo.status} />
                    </TableCell>
                    <TableCell>{algo.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Stack>
      )}

      {!result && !loading && !error && (
        <Typography color="text.secondary">Click "Run Diagnostics" to check readiness.</Typography>
      )}
    </Box>
  );
}

export default PreMarketDiagnosticsPage;
